import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, X, Home, Send, Download, CreditCard, TrendingUp, Link as LinkIcon, 
  History, Settings, Users, Wallet, User as UserIcon, LogOut, Coins, ArrowLeftRight 
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';

const STORAGE_KEY = 'modanmic_feature_flags';
const loadFlags = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};
const isFeatureEnabled = (flags, username, key) => {
  const userOverride = (flags.users || {})[username];
  if (userOverride && userOverride[key] !== undefined) return userOverride[key];
  const global = flags.global || {};
  return global[key] !== false; // default ON
};

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(loadFlags);

  // Re-read flags when menu opens (picks up admin changes)
  useEffect(() => {
    if (isMenuOpen) setFeatureFlags(loadFlags());
  }, [isMenuOpen]);

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  // Shift+A → Admin dashboard (only for authorised emails)
  const ADMIN_EMAILS = ['michaelawofeko@gmail.com', 'michaeloluwatosin321@gmail.com'];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.shiftKey && e.key === 'A') {
        const email = user?.email?.trim().toLowerCase();
        if (email && ADMIN_EMAILS.includes(email)) {
          navigate('/admin');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, navigate]);

  const ALL_MENU_ITEMS = [
    { icon: Home,         label: 'Dashboard',         path: '/dashboard',          featureKey: null },
    { icon: Send,         label: 'Send Money',         path: '/send',               featureKey: 'send_money' },
    { icon: Download,     label: 'Receive',            path: '/receive',            featureKey: 'receive_money' },
    { icon: CreditCard,   label: 'Withdraw',           path: '/withdraw',           featureKey: 'withdraw' },
    { icon: Coins,        label: 'PENE Dashboard',     path: '/investment-dashboard', featureKey: 'pene_dashboard' },
    { icon: TrendingUp,   label: 'Subscription Plans', path: '/investment',         featureKey: 'subscription_plans' },
    { icon: ArrowLeftRight, label: 'PENE Exchange',    path: '/exchange',           featureKey: 'pene_exchange' },
    { icon: LinkIcon,     label: 'Payment Link',       path: '/payment-link',       featureKey: 'payment_link' },
    { icon: History,      label: 'History',            path: '/history',            featureKey: 'history' },
    { icon: Users,        label: 'Referral',           path: '/referral',           featureKey: null },
    { icon: UserIcon,     label: 'Account',            path: '/account',            featureKey: null },
    { icon: Settings,     label: 'Settings',           path: '/settings',           featureKey: null },
  ];

  const menuItems = ALL_MENU_ITEMS.filter(item => {
    if (!item.featureKey) return true; // always show Dashboard/Account/Settings
    return isFeatureEnabled(featureFlags, user?.telegramName, item.featureKey);
  });

  const isActive = (path) => location.pathname === path;

  // Guard: if not logged in, render nothing (ProtectedRoute handles redirect)
  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 fixed top-0 left-0 right-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Menu Button */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div 
                className="text-lg font-bold text-slate-900 cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                MODANMIC
              </div>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-slate-900">{user.telegramName}</p>
                <p className="text-xs text-slate-600">Welcome back</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                {user.telegramName?.charAt(1)?.toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Menu - Slides in from left */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${
          isMenuOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Overlay */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Sidebar */}
        <aside
          className={`absolute top-0 left-0 bottom-0 w-72 bg-white shadow-xl transform transition-transform duration-300 ${
            isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Sidebar Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {user.telegramName?.charAt(1)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{user.telegramName}</p>
                  <p className="text-xs text-slate-600">{user.email || 'Account holder'}</p>
                </div>
              </div>
              <button
                onClick={() => { setIsMenuOpen(false); navigate('/'); }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Back to Home
              </button>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        active
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-slate-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all"
              >
                <LogOut size={20} />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
